class SeriesPageVideo extends HTMLElement {
  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('video'));
    this.toggleButton = this.querySelector('[data-video-toggle]');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.videoListeners = new Map();

    this.handleToggle = this.handleToggle.bind(this);
    this.handleEnvironmentChange = this.handleEnvironmentChange.bind(this);

    this.toggleButton?.addEventListener('click', this.handleToggle);
    this.motionQuery.addEventListener('change', this.handleEnvironmentChange);
    this.mobileQuery.addEventListener('change', this.handleEnvironmentChange);

    this.videos.forEach((video) => {
      const handlePlay = () => this.updatePlaybackState(video, true);
      const handlePause = () => this.updatePlaybackState(video, false);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('ended', handlePause);
      this.videoListeners.set(video, { handlePlay, handlePause });
    });

    this.handleEnvironmentChange();
  }

  getActiveVideo() {
    const mobileVideo = this.querySelector('.series-page-video__media--mobile');
    const desktopVideo = this.querySelector('.series-page-video__media--desktop');

    if (this.mobileQuery.matches && mobileVideo) {
      return mobileVideo;
    }

    return desktopVideo || mobileVideo;
  }

  handleEnvironmentChange() {
    const activeVideo = this.getActiveVideo();

    this.videos.forEach((video) => {
      if (video !== activeVideo) {
        video.pause();
      }
    });

    if (!activeVideo || this.motionQuery.matches) {
      activeVideo?.pause();
      this.updatePlaybackState(activeVideo, false);
      return;
    }

    activeVideo.play().catch(() => {
      this.updatePlaybackState(activeVideo, false);
    });
  }

  handleToggle() {
    const activeVideo = this.getActiveVideo();
    if (!activeVideo) return;

    if (activeVideo.paused) {
      activeVideo.play().catch(() => {
        this.updatePlaybackState(activeVideo, false);
      });
      return;
    }

    activeVideo.pause();
  }

  updatePlaybackState(video, isPlaying) {
    if (video && video !== this.getActiveVideo()) return;

    this.classList.toggle('is-playing', isPlaying);
    if (!this.toggleButton) return;

    this.toggleButton.setAttribute('aria-pressed', String(isPlaying));
    this.toggleButton.setAttribute(
      'aria-label',
      isPlaying ? this.dataset.pauseLabel : this.dataset.playLabel
    );
  }

  disconnectedCallback() {
    this.toggleButton?.removeEventListener('click', this.handleToggle);
    this.motionQuery?.removeEventListener('change', this.handleEnvironmentChange);
    this.mobileQuery?.removeEventListener('change', this.handleEnvironmentChange);

    this.videos?.forEach((video) => {
      const listeners = this.videoListeners.get(video);
      if (listeners) {
        video.removeEventListener('play', listeners.handlePlay);
        video.removeEventListener('pause', listeners.handlePause);
        video.removeEventListener('ended', listeners.handlePause);
      }
      video.pause();
    });

    this.videoListeners?.clear();
  }
}

if (!customElements.get('series-page-video')) {
  customElements.define('series-page-video', SeriesPageVideo);
}
